
import { useState } from "react";
import Papa from "papaparse";
import { Lead } from "@/types";
import { ColumnMapping, PapaParseError } from "./types";
import { toast } from "@/hooks/use-toast";

export const useCSVProcessing = () => {
  const parseCSVPreview = (file: File, onSuccess: (headers: string[], previewData: string[][]) => void, onError: (error: string) => void) => {
    try {
      Papa.parse(file, {
        preview: 5, // Preview first 5 rows
        complete: (results) => {
          try {
            const data = results.data as string[][];
            if (data.length > 0) {
              const headers = data[0];
              const previewData = data.slice(1);
              onSuccess(headers, previewData);
            } else {
              throw new Error("CSV file appears to be empty or malformed");
            }
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error parsing CSV preview";
            console.error("CSV preview error:", err);
            onError(`Error parsing CSV preview: ${errorMessage}`);
            toast({
              title: "Error parsing CSV",
              description: errorMessage,
              variant: "destructive",
            });
          }
        },
        error: (error: PapaParseError) => {
          console.error("Papa Parse error:", error);
          const errorDetails = error.code 
            ? `${error.message} (code: ${error.code})`
            : error.message;
          onError(`CSV parsing error: ${errorDetails}`);
          toast({
            title: "Error parsing CSV",
            description: errorDetails,
            variant: "destructive",
          });
        }
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error initializing parser";
      console.error("CSV parser initialization error:", err);
      onError(`Parser initialization error: ${errorMessage}`);
      toast({
        title: "CSV parser error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const processCSVFile = (
    file: File, 
    columnMapping: ColumnMapping, 
    dealerId: string,
    onSuccess: (leads: Lead[]) => void,
    onError: (error: string) => void
  ) => {
    try {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          try {
            const parsedData = results.data as Record<string, string>[];
            console.log("Parsed CSV data:", parsedData.slice(0, 2)); // Log first 2 rows for debugging
            
            if (results.errors && results.errors.length > 0) {
              const errorDetails = results.errors.map(e => `${e.message} at row ${e.row || 'unknown'}`).join(', ');
              console.warn("CSV parsing had errors:", results.errors);
              onError(`CSV had parsing issues: ${errorDetails}`);
              toast({
                title: "CSV had parsing issues",
                description: errorDetails,
                variant: "destructive",
              });
            }
            
            // Map the CSV data to our Lead structure
            const leads: Lead[] = parsedData
              .filter(row => Object.keys(row).length > 0) // Filter out empty rows
              .map((row) => {
                console.log("Processing row:", row);
                // Debugging validation
                const name = row[columnMapping.name];
                const email = row[columnMapping.email];
                const phone = row[columnMapping.phone];
                
                if (!name) console.warn("Missing name in row:", row);
                if (!email) console.warn("Missing email in row:", row);
                if (!phone) console.warn("Missing phone in row:", row);
                
                const lead: Lead = {
                  name: name || "",
                  email: email || "",
                  phone: phone || "",
                  meta_notes: columnMapping.meta_notes ? row[columnMapping.meta_notes] || "" : "",
                  dealer_id: dealerId,
                  status: "queued",
                };
                
                return lead;
              }).filter(lead => {
                const isValid = lead.name && lead.email && lead.phone;
                if (!isValid) {
                  console.warn("Filtered out invalid lead:", lead);
                }
                return isValid;
              });

            console.log(`Found ${leads.length} valid leads out of ${parsedData.length} rows`);
            
            if (leads.length === 0) {
              throw new Error(`No valid leads found after mapping from ${parsedData.length} rows. Check that your CSV contains the required fields and they're mapped correctly.`);
            }

            onSuccess(leads);
            toast({
              title: "CSV processed successfully",
              description: `Found ${leads.length} valid leads`,
            });
          } catch (error) {
            console.error("Error processing CSV:", error);
            const errorMessage = error instanceof Error 
              ? error.message 
              : "Failed to process CSV";
            onError(errorMessage);
            toast({
              title: "Error processing CSV",
              description: errorMessage,
              variant: "destructive",
            });
          }
        },
        error: (error: PapaParseError) => {
          console.error("Papa Parse complete file error:", error);
          const errorDetails = error.code 
            ? `${error.message} (code: ${error.code})` 
            : error.message;
          onError(`CSV parsing error: ${errorDetails}`);
          toast({
            title: "Error parsing CSV",
            description: errorDetails,
            variant: "destructive",
          });
        }
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error processing file";
      console.error("CSV processing error:", err);
      onError(errorMessage);
      toast({
        title: "Error processing CSV",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return {
    parseCSVPreview,
    processCSVFile
  };
};
